import { useState, useCallback, type ReactNode } from "react";
import styled from "styled-components";
import { spacing } from "@styling/sizing";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { RestStore } from "@KassaroAB/shared/types/store";
import type { ChainId, ResellerId } from "@KassaroAB/shared/types/ids";
import { Container } from "../../components/Container";
import { Card } from "../../components/Card";
import { LoadingContainer } from "../../components/LoadingContainer";
import { LoadMoreButton } from "../../components/LoadMoreButton";
import { EmptyState } from "../../components/EmptyState";
import { Title } from "../../components/Title";
import { Button } from "../../components/Button";
import { SidebarPanel } from "../../components/SidebarPanel";
import { StoreForm } from "./StoreForm";
import { useStoreSearchKeyQuery } from "../../query/storesQuery";
import { useGetUserQuery } from "../../query/usersQuery";
import { isAlgoliaSearchRole } from "../../utils/isAlgoliaSearchRole";
import { useStoreSearch } from "../../hooks/useStoreSearch";
import { StoreListSearchRow } from "../../components/StoreListSearchRow";
import { List } from "../../components/List";
import { ListItem } from "../../components/ListItem";

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
`;

export type StoreListPageCreateInput = {
  name: string;
  displayName: string;
  street: string;
  postalcode: string;
  city: string;
  country: string;
  currency: string;
  language: string;
  timeZone?: string;
  orgNumber: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  active?: boolean;
};

type StoreListPageProps = {
  allStores: RestStore[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  searchChainId?: ChainId;
  searchResellerId?: ResellerId;
  onCreateStore: (data: StoreListPageCreateInput) => Promise<RestStore>;
  createLoading: boolean;
  headerActions?: ReactNode;
};

export const StoreListPage = ({
  allStores,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  searchChainId,
  searchResellerId,
  onCreateStore,
  createLoading,
  headerActions,
}: StoreListPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") ?? "";
  const activeOnlyFromUrl = searchParams.get("activeOnly") !== "false";

  const { data: user } = useGetUserQuery();
  const useAlgolia = user ? isAlgoliaSearchRole(user.role) : false;
  const searchKeyQuery = useStoreSearchKeyQuery(user, useAlgolia);
  const searchConfig = searchKeyQuery.data ?? null;
  const storeSearch = useStoreSearch(searchConfig, {
    chainId: searchChainId,
    resellerId: searchResellerId,
    defaultActiveOnly: true,
    syncSearchUrl: true,
    urlQuery: qParam,
    urlActiveOnly: activeOnlyFromUrl,
  });

  const pushSearchToUrl = useCallback(
    (next: { q?: string; activeOnly?: boolean }) => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          if (next.q !== undefined) {
            if (next.q.length > 0) {
              nextParams.set("q", next.q);
            } else {
              nextParams.delete("q");
            }
          }
          if (next.activeOnly !== undefined) {
            if (next.activeOnly) {
              nextParams.delete("activeOnly");
            } else {
              nextParams.set("activeOnly", "false");
            }
          }
          return nextParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleQueryChange = (value: string) => {
    storeSearch.updateQuery(value);
    if (useAlgolia && searchConfig) {
      pushSearchToUrl({ q: value });
    }
  };

  const handleToggleActiveOnly = () => {
    const nextActiveOnly = !storeSearch.activeOnly;
    storeSearch.toggleActiveOnly();
    if (useAlgolia && searchConfig) {
      pushSearchToUrl({ activeOnly: nextActiveOnly });
    }
  };

  const useSearchResults =
    useAlgolia && !!searchConfig && storeSearch.query.length > 0;
  const displayItems = useSearchResults
    ? storeSearch.hits.map((h) => {
        const display = (h.displayName ?? "").trim();
        const name = (h.name ?? "").trim();
        const title = display || name;
        const subTitle =
          display.length > 0 && name.length > 0 && display !== name
            ? name
            : undefined;
        const org = (h.orgNumber ?? "").trim();
        return {
          id: h.id,
          title,
          subTitle,
          trailing: org || undefined,
          inactive: !h.active,
        };
      })
    : allStores.map((s) => {
        const display = (s.displayName ?? "").trim();
        const name = (s.name ?? "").trim();
        const title = display || name;
        const subTitle =
          display.length > 0 && name.length > 0 && display !== name
            ? name
            : undefined;
        const org = (s.orgNumber ?? "").trim();
        return {
          id: s.id,
          title,
          subTitle,
          trailing: org || undefined,
          inactive: !s.active,
        };
      });

  if (isLoading && allStores.length === 0 && !useSearchResults) {
    return (
      <Container>
        <PageHeader>
          <Title>{t("listPages.storesTitle")}</Title>
        </PageHeader>
        <LoadingContainer message={t("listPages.storesLoading")} />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <Title>{t("listPages.storesTitle")}</Title>
        <HeaderActions>
          {headerActions}
          <Button onClick={() => setIsPanelOpen(true)}>{t("listPages.addStore")}</Button>
        </HeaderActions>
      </PageHeader>

      {useAlgolia && searchConfig ? (
        <StoreListSearchRow
          query={storeSearch.query}
          onQueryChange={handleQueryChange}
          activeOnly={storeSearch.activeOnly}
          onToggleActiveOnly={handleToggleActiveOnly}
          placeholder={t("listPages.searchStoresPlaceholder")}
          activeOnlyLabel={t("listPages.activeStoresOnly")}
        />
      ) : null}

      {useSearchResults && storeSearch.isSearching && displayItems.length === 0 ? (
        <LoadingContainer message={t("listPages.storesLoading")} />
      ) : !useSearchResults && allStores.length === 0 ? (
        <Card>
          <EmptyState message={t("listPages.emptyStores")} />
        </Card>
      ) : useSearchResults && displayItems.length === 0 ? (
        <Card>
          <EmptyState message={t("listPages.storesSearchNoResults")} />
        </Card>
      ) : (
        <>
          <List dense>
            {displayItems.map((item) => (
              <ListItem
                key={item.id}
                title={item.title}
                subTitle={item.subTitle}
                trailing={item.trailing}
                inactive={item.inactive}
                compact
                to={`/stores/${item.id}`}
              />
            ))}
          </List>
          {useSearchResults ? null : (
            <LoadMoreButton
              onClick={() => fetchNextPage()}
              isLoading={isFetchingNextPage}
              hasMore={hasNextPage}
            />
          )}
        </>
      )}

      <SidebarPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        title={t("listPages.createStorePanel")}
      >
        <StoreForm
          onSubmit={(value) =>
            onCreateStore({
              name: value.name,
              displayName: value.displayName,
              street: value.street,
              postalcode: value.postalcode,
              city: value.city,
              country: value.country,
              currency: value.currency,
              language: value.language,
              timeZone: value.timeZone || undefined,
              orgNumber: value.orgNumber,
              contactPerson: value.contactPerson,
              contactPhone: value.contactPhone,
              contactEmail: value.contactEmail,
              active: value.active,
            }).then((store) => {
              localStorage.setItem("selectedStoreId", store.id);
              setIsPanelOpen(false);
              navigate(`/stores/${store.id}`);
            })
          }
          onCancel={() => setIsPanelOpen(false)}
          isLoading={createLoading}
          submitLabel={t("storeForm.createStore")}
          submitLoadingLabel={t("storeForm.creating")}
        />
      </SidebarPanel>
    </Container>
  );
};
